import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from '@reduxjs/toolkit';
import { LoanVariablesDialog } from 'components/Modals/LoanVariablesDialog/LoanVariablesDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getLoanVariablesDialogState } from 'store/selectors/loanVariablesDialog.selector';
import { useFormContext } from 'react-hook-form';
import {
  IRACAffordabilityEngineForm,
  TRADE_IN_OVERRIDDEN,
} from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import { getCreatedUserVehicle, getAddedIsUserVehicle } from 'store/selectors/vehicle.selector';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { getFeaturesList, getVendorName } from 'store/selectors/ui.selector';
import { getUser } from 'store/selectors/auth.selector';
import { logToDatadog } from 'utils/datadog';
import { DatadogEvents } from 'utils/datadogEvents';
import { AppDispatch } from 'store/store';
import { RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER } from 'modules/core/constants/mappers/submitVehicles.mapper';
import { getLoanTermsListThunk, getLoanVariablesInfoThunk } from 'actions/vehicleActions';
import { RequestStatus, VehicleType } from 'modules/core/enums';
import { getSubmitVehicleObjectQuery } from 'modules/core/utils';
import {
  getLoanVariablesCurrentVehicle,
  getDealSourceData,
  getDealSourceDataFlags,
  getRACSortedVehicles,
  getVehicleFetchingFlags,
} from 'store/selectors/RACAffordabilityEngine.selector';
import { resetDealSourceDataFlags } from 'store/reducers/RACAffordabilityEngine.reducer';
import {
  setIsFetchingFailed,
  setIsLoading,
  setLoanTermsList,
  setLoanVariables,
} from 'store/reducers/loanVariablesDialog.reducer';
import {
  AVAILABLE_DEAL_STRUCTURE_STATUSES,
  EMPTY_LTV_CATEGORY,
} from 'components/Modals/LoanVariablesDialog/consts/consts';
import { IAvailableDealStructureStatus } from 'components/Modals/LoanVariablesDialog/types';

import { RACAffordabilityEngineService } from './RACAffordabilityEngine.service';

export interface IRACLoanVariablesDialog {
  isLoanVariablesDialogActive: boolean;
  isDealSourceCar?: boolean;
  resetState: () => void;
  onHide: () => void;
  isMakeItWork: boolean;
  isSubmitR1DealBlockedByControls?: boolean;
}

export const RACLoanVariablesDialog: FC<IRACLoanVariablesDialog> = ({
  isLoanVariablesDialogActive,
  isDealSourceCar,
  resetState,
  onHide,
  isMakeItWork,
  isSubmitR1DealBlockedByControls,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const dealSourceData = useSelector(getDealSourceData);
  const dealSourceDataFlags = useSelector(getDealSourceDataFlags);
  const currentVehicleData = useSelector(getLoanVariablesCurrentVehicle);
  const { loanVariables } = useSelector(getLoanVariablesDialogState);
  const vendorName = useSelector(getVendorName);
  const createdUserVehicle = useSelector(getCreatedUserVehicle);
  const { is_calculated_book_value } = useSelector(getFeaturesList);
  const isAddedUserVehicle = useSelector(getAddedIsUserVehicle);
  const user = useSelector(getUser);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const modalId = useMemo(() => nanoid(), [currentVehicleData?.id]);

  const { getValues: commonGetValues } = useFormContext<IRACAffordabilityEngineForm>();

  const [formHistory, setFormHistory] = useState<any[]>([]);

  const { desiredMonthlyPayment, feesAndTaxes, tier } = commonGetValues() as any;

  const { tier: tierName } = tier;
  const isLtvCategoriesExists = loanVariables?.ltv_categories && loanVariables.ltv_categories.length > 0;
  const isDealSourceVehicle =
    ((loanVariables?.id || currentVehicleData?.id) &&
      (loanVariables?.id || currentVehicleData?.id) === dealSourceData?.vehicle_id) ||
    isDealSourceCar;
  const isWholesaleInventory = user?.withWholesaleInventory;
  const r1BookValue = dealSourceData?.sale_vehicle?.book_value;
  const manuallyCreated =
    currentVehicleData?.manually_created ||
    (isDealSourceVehicle && dealSourceData?.manually_created) ||
    isAddedUserVehicle;
  const isHideGuidebookValue =
    (is_calculated_book_value &&
      !isDealSourceVehicle &&
      !manuallyCreated &&
      currentVehicleData?.condition?.toLowerCase() === VehicleType.USED) ||
    (isWholesaleInventory &&
      !(
        isMakeItWork &&
        typeof r1BookValue === 'number' &&
        Number.isFinite(r1BookValue) &&
        r1BookValue > 0 &&
        isDealSourceVehicle
      ));

  const getTargetPayment = useCallback(() => desiredMonthlyPayment, [desiredMonthlyPayment]);

  const getCurrentPayment = useCallback(
    () => (isLtvCategoriesExists ? loanVariables.ltv_categories[0].payments : NaN),
    [isLtvCategoriesExists, loanVariables?.ltv_categories],
  );

  const getTotalAmountFinanced = useCallback(
    () => (isLtvCategoriesExists ? loanVariables.ltv_categories[0].loan_amount : NaN),
    [isLtvCategoriesExists, loanVariables?.ltv_categories],
  );

  const getLoanVariablesData = useCallback(
    async (defaultValues) => {
      const values = commonGetValues();
      const { totalNetDownPaymentDialog, cashDownOverridden, [TRADE_IN_OVERRIDDEN]: tradeInOverridden } = values as any;
      const dealSourceCashDownPayment =
        (dealSourceData?.finance_details?.cash_down || 0) + (dealSourceData?.finance_details?.rebates || 0);
      const cashDownPayment = cashDownOverridden ? totalNetDownPaymentDialog?.cashDown || 0 : dealSourceCashDownPayment;
      const tradeValue = tradeInOverridden
        ? totalNetDownPaymentDialog?.tradeValue || 0
        : dealSourceData?.trade_in_vehicle?.value;
      const amountOwed = tradeInOverridden
        ? totalNetDownPaymentDialog?.amountOwed || 0
        : dealSourceData?.trade_in_vehicle?.amount_owed;

      const vehicleParameters = getSubmitVehicleObjectQuery<IRACAffordabilityEngineForm>(
        { ...currentVehicleData, ...values, vendorName: vendorName, isLender: false },
        RACAffordabilityEngineService.getSubmitQueryForLoanVariables,
        RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER,
      );

      const params = {
        ...vehicleParameters,
        vehicle_id: currentVehicleData?.id,
        guidebook_value: currentVehicleData?.guidebook_value,
        sales_fees: undefined,
        estimated_fees: feesAndTaxes.estimatedFees || 0,
        year: undefined,
        ...(isDealSourceVehicle && {
          ...(dealSourceData.taxes_fees?.taxes && { taxes: dealSourceData.taxes_fees.taxes || 0 }),
          vehicle_id: dealSourceData.vehicle_id,
          guidebook_value: dealSourceData.sale_vehicle.book_value,
          taxes: dealSourceData.taxes_fees?.taxes || 0,
          add_ons: 0,
          amount_owed: amountOwed,
          cash_down_payment: cashDownPayment,
          current_price: dealSourceData.sale_vehicle.retail_price,
          loan_term: dealSourceData.finance_details.term || 0,
          net_down_payment: (tradeValue || 0) - (amountOwed || 0),
          odometer: dealSourceData.sale_vehicle.mileage,
          trade_value: tradeValue,
          sales_tax_rate: feesAndTaxes.salesTaxRate,
          year: undefined,
        }),
        ...(isAddedUserVehicle && {
          vehicle_id: createdUserVehicle.id,
          guidebook_value: createdUserVehicle.book_value,
          current_price: createdUserVehicle.current_price,
          odometer: createdUserVehicle.odometer,
          loan_term: createdUserVehicle.loan_term,
          year: undefined,
          taxes: undefined,
        }),
        apr: undefined,
        override_vsc: dealSourceData?.finance_details?.override_vsc,
        total_ltv_limit: dealSourceData?.finance_details?.total_ltv_limit,
        ...(isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
      };

      const loanVariablesInfoResponse = await dispatch(getLoanVariablesInfoThunk(params));
      const loanTermsListResponse = await dispatch(
        getLoanTermsListThunk({
          mileage: isAddedUserVehicle
            ? createdUserVehicle?.odometer
            : currentVehicleData?.odometer || dealSourceData?.sale_vehicle?.mileage,
          tier: tierName,
          ...(isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
        }),
      );

      const isLoanVariablesRejected = loanVariablesInfoResponse?.meta?.requestStatus === RequestStatus.REJECTED;
      const isLoanTermsRejected = loanTermsListResponse?.meta?.requestStatus === RequestStatus.REJECTED;

      if (isLoanVariablesRejected || isLoanTermsRejected) {
        const error = isLoanVariablesRejected
          ? (loanVariablesInfoResponse as any)?.payload
          : (loanTermsListResponse as any)?.payload;

        logToDatadog(
          DatadogEvents.LOG_CATEGORIES[DatadogEvents.FAILED_TO_RETRIEVE_DATA],
          {
            log_category: DatadogEvents.FAILED_TO_RETRIEVE_DATA,
            vehicle: currentVehicleData,
            error_details: {
              message: error?.message,
              status: error?.response?.status,
              statusText: error?.response?.statusText,
              data: error?.response?.data,
            },
          },
          user,
        );
        dispatch(setIsFetchingFailed({ modalId: modalId, isFetchingFailed: true }));
      }

      dispatch(setLoanTermsList({ modalId: modalId, loanTermsList: loanTermsListResponse.payload }));
      dispatch(setLoanVariables({ modalId: modalId, loanVariables: loanVariablesInfoResponse.payload }));

      const loanVariablesInfo: any = loanVariablesInfoResponse.payload;
      const isLtvCategoriesExists = loanVariablesInfo?.ltv_categories && loanVariablesInfo.ltv_categories.length > 0;
      const dealBookValue = dealSourceData?.sale_vehicle?.book_value;
      const shouldUseDealBookValue =
        typeof dealBookValue === 'number' && Number.isFinite(dealBookValue) && dealBookValue > 0;
      let guidebookValueForHistory = defaultValues.loanVariablesForm.guidebookValue;

      if (isAddedUserVehicle) {
        guidebookValueForHistory = createdUserVehicle?.book_value ?? guidebookValueForHistory;
      } else if (isDealSourceVehicle) {
        guidebookValueForHistory =
          (shouldUseDealBookValue ? dealBookValue : loanVariablesInfo?.guidebook_value) ?? guidebookValueForHistory;
      } else if (!isHideGuidebookValue) {
        guidebookValueForHistory = loanVariablesInfo?.guidebook_value ?? guidebookValueForHistory;
      }

      setFormHistory([
        {
          loanVariablesForm: {
            ...defaultValues.loanVariablesForm,
            loanTerm: {
              title: loanVariablesInfo?.estimated_term || loanTermsListResponse.payload[0],
              value: loanVariablesInfo?.estimated_term || loanTermsListResponse.payload[0],
            },
            loanTermsList: loanTermsListResponse.payload,
            apr: isLtvCategoriesExists ? loanVariablesInfo.ltv_categories[0].rate : NaN,
            totalAmountFinanced: isLtvCategoriesExists ? loanVariablesInfo.ltv_categories[0].loan_amount : NaN,
            taxes: loanVariablesInfo.taxes || 0,
            ltvCategories: isLtvCategoriesExists ? loanVariablesInfo.ltv_categories : [],
            currentPayment: isLtvCategoriesExists ? loanVariablesInfo.ltv_categories[0].payments : [],
            guidebookValue: guidebookValueForHistory,
          },
        },
      ]);
      dispatch(setIsLoading({ modalId: modalId, isLoading: false }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      commonGetValues,
      currentVehicleData,
      dispatch,
      vendorName,
      isDealSourceVehicle,
      dealSourceData,
      createdUserVehicle,
      desiredMonthlyPayment,
    ],
  );

  const { t } = useTranslation();

  const getActualPaymentInfoMessage = useCallback(
    (loanVariables: any) => t('components.loanVariablesModal.infoMessages.actualPayment'),
    [t],
  );

  const MAX_PAGE_SIZE = 4;

  const getLtvCategories = useCallback(
    (loanVariables: any, availableDealStructureStatus: IAvailableDealStructureStatus) => {
      if (availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO) {
        return new Array(MAX_PAGE_SIZE).fill(EMPTY_LTV_CATEGORY);
      } else {
        return loanVariables.ltv_categories;
      }
    },
    [],
  );

  return (
    <LoanVariablesDialog
      modalId={modalId}
      dealSourceData={dealSourceData}
      dealSourceDataFlags={dealSourceDataFlags}
      resetDealSourceDataFlags={resetDealSourceDataFlags}
      headerLogoSrc={STATIC_IMAGE_URLS.racLogo}
      isLender={false}
      isRac={true}
      isLoanVariablesDialogActive={isLoanVariablesDialogActive}
      resetState={resetState}
      onHide={onHide}
      isDealSourceCar={isDealSourceCar}
      manuallyCreated={manuallyCreated}
      isAddedUserVehicle={isAddedUserVehicle}
      isMakeItWork={isMakeItWork}
      isSubmitR1DealBlockedByControls={isSubmitR1DealBlockedByControls}
      getLoanVariablesData={getLoanVariablesData}
      formHistory={formHistory}
      setFormHistory={setFormHistory}
      loanTermValue={loanVariables?.estimated_term}
      ltvCategories={isLtvCategoriesExists ? loanVariables.ltv_categories : []}
      apr={isLtvCategoriesExists ? loanVariables.ltv_categories[0].rate : NaN}
      guidebookValue={isHideGuidebookValue ? 0 : currentVehicleData?.guidebook_value || 0}
      getTargetPayment={getTargetPayment}
      getTotalAmountFinanced={getTotalAmountFinanced}
      getCurrentPayment={getCurrentPayment}
      sortedVehiclesSelector={getRACSortedVehicles}
      vehiclesFetchingFlagsSelector={getVehicleFetchingFlags}
      shouldRenderRouteOneUserId={false}
      getActualPaymentInfoMessage={getActualPaymentInfoMessage}
      getLtvCategories={getLtvCategories}
      isRouteOneFlow={isMakeItWork}
    />
  );
};
